"use client"

import { useIsActiveLink } from "@src/hooks/useIsActiveLink"
import { MenuSquaresIcon } from "@src/icons"
import clsx from "clsx"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useCallback, useState } from "react"
import { AdaptiveOverlay } from "../AdaptiveOverlay"
import Drawer from "../Drawer"
import type { NavLinkItemType } from "./NavbarDesktop"
import { isNavLinkItem, useVisibleNavItems } from "./NavbarDesktop"

const focusedRoutePrefixes = [
  "/send",
  "/receive",
  "/transfer",
  "/settings",
  "/referrals",
]

type NavItemProps = Pick<
  NavLinkItemType,
  "icon" | "iconSolid" | "label" | "shortLabel"
> & {
  href?: string
  isActive?: boolean
  onClick?: () => void
}

const NavItem = ({
  label,
  shortLabel,
  icon,
  iconSolid,
  isActive = false,
  href,
  onClick,
}: NavItemProps) => {
  const className =
    "group flex flex-col items-center justify-center gap-1 py-3 focus-visible:outline-hidden"

  const IconComponent = isActive && iconSolid ? iconSolid : icon

  const content = (
    <>
      <IconComponent
        className={clsx(
          "size-5 shrink-0",
          isActive
            ? "text-gray-900 dark:text-white"
            : "text-gray-400 dark:text-gray-500"
        )}
      />
      <span
        className={clsx(
          "text-center text-xs/none",
          isActive
            ? "font-bold text-gray-900 dark:text-white"
            : "font-medium text-gray-400 dark:text-gray-500"
        )}
      >
        {shortLabel ?? label}
      </span>
    </>
  )

  if (href) {
    return (
      <Link href={href} className={className}>
        {content}
      </Link>
    )
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  )
}

export function NavbarMobile() {
  const [open, setOpen] = useState(false)
  const onClose = useCallback(() => setOpen(false), [])

  const pathname = usePathname()
  const { isActive } = useIsActiveLink()
  const hideNav =
    pathname.startsWith("/perps/") ||
    focusedRoutePrefixes.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    )

  const visibleItems = useVisibleNavItems()
  const navLinkItems = visibleItems.flatMap((item) =>
    isNavLinkItem(item) ? [item] : item.children
  )
  const primaryNavItems = navLinkItems.filter((item) => item.showInTabs)
  const itemsAmount = primaryNavItems.length + 1
  const drawerNavItems = navLinkItems.filter((item) => item.href !== "/home")

  return (
    <>
      {!hideNav && (
        <nav className="fixed inset-x-0 bottom-0 z-20 flex items-center justify-center border-gray-300 border-t bg-white px-4 pb-safe sm:px-6 xl:hidden dark:border-white/10 dark:bg-gray-800">
          <div
            className="grid w-full max-w-[464px]"
            style={{
              gridTemplateColumns: `repeat(${itemsAmount}, minmax(0, 1fr))`,
            }}
          >
            {primaryNavItems.map((item) => (
              <NavItem
                key={item.label}
                {...item}
                isActive={isActive(item.href)}
              />
            ))}

            <NavItem
              label="Menu"
              icon={MenuSquaresIcon}
              onClick={() => setOpen(true)}
            />
          </div>
        </nav>
      )}

      <AdaptiveOverlay
        open={open}
        onClose={onClose}
        desktop={null}
        mobile={{
          type: "drawer",
        }}
      >
        <Drawer.Title className="sr-only">Menu</Drawer.Title>

        <div className="grid grid-cols-2 gap-2">
          {drawerNavItems.map(({ label, href, icon: Icon }) => (
            <Link
              key={label}
              href={href}
              onClick={onClose}
              className={clsx(
                "group flex flex-col items-start justify-between gap-5 rounded-2xl p-4 text-gray-900 outline-hidden dark:text-white",
                isActive(href)
                  ? "bg-gray-200 dark:bg-white/15"
                  : "bg-gray-100 dark:bg-white/5"
              )}
            >
              <Icon className="size-5 shrink-0" />
              <span className="flex-1 font-semibold text-base/5">{label}</span>
            </Link>
          ))}
        </div>
      </AdaptiveOverlay>
    </>
  )
}
